import { useEffect, useState } from 'react';
import MaterielCommercePicker from './MaterielCommercePicker';
import {
  deviceTitle,
  typeLabel,
} from '../../materiels/materielDisplayMaps';
import { friendlyMaterielActionError } from '../../materiels/materielActionErrors';

/**
 * Modal Affecter / Changer de point de vente — recherche par nom.
 */
export default function MaterielAssignModal({
  open,
  item,
  onClose,
  onSubmit,
  commerces = [],
  commercesLoading = false,
  commercesError = null,
  onRetryCommerces,
}) {
  const [commerce, setCommerce] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    if (!open) return;
    setCommerce(null);
    setSubmitting(false);
    setFieldError('');
  }, [open, item?.id]);

  if (!open || !item) return null;

  const isChange =
    item.status === 'AFFECTE' && Boolean(item.commercant?.nom?.trim());
  const currentNom = isChange ? item.commercant.nom : null;
  const title = isChange
    ? 'Changer de point de vente'
    : 'Affecter à un point de vente';

  const requestClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (submitting) return;
    if (!commerce?.id) {
      setFieldError('Sélectionnez un point de vente.');
      return;
    }
    setFieldError('');
    setSubmitting(true);
    try {
      await onSubmit?.({ commerce });
    } catch (err) {
      setFieldError(
        friendlyMaterielActionError(
          err,
          'Impossible d’affecter ce matériel. Réessayez.'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Fermer"
        disabled={submitting}
        onClick={requestClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-md bg-white rounded-lg shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto p-4 space-y-4"
      >
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {deviceTitle(item)} · {typeLabel(item.type)}
              {item.serialNumber ? ` · N° série · ${item.serialNumber}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={submitting}
            className="text-xs text-gray-500 hover:underline disabled:opacity-50"
          >
            Fermer
          </button>
        </div>

        {isChange ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            Changement d&apos;affectation — point de vente actuel :{' '}
            <span className="font-medium">{currentNom}</span>
          </div>
        ) : null}

        <MaterielCommercePicker
          commerces={commerces}
          value={commerce}
          onChange={setCommerce}
          disabled={submitting}
          loading={commercesLoading}
          error={commercesError}
          onRetry={onRetryCommerces}
        />

        {commerce ? (
          <div className="rounded-md border border-primary-200 bg-primary-50/60 px-3 py-2 text-sm text-gray-800 space-y-1">
            {isChange ? (
              <p>
                Nouveau point de vente :{' '}
                <span className="font-medium">{commerce.nom}</span>
              </p>
            ) : (
              <p>
                Affecter à <span className="font-medium">{commerce.nom}</span> ?
              </p>
            )}
            {isChange ? (
              <p className="text-xs text-gray-600">
                L&apos;ancien point de vente ({currentNom}) ne sera plus lié à
                ce matériel.
              </p>
            ) : null}
          </div>
        ) : null}

        {fieldError ? (
          <p className="text-sm text-red-700" role="alert">
            {fieldError}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={requestClose}
            disabled={submitting}
            className="px-3 py-2 text-sm text-gray-700 hover:underline disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {submitting
              ? 'Affectation…'
              : isChange
                ? 'Confirmer le changement'
                : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
}
